import React, { useState } from "react";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const CustomTextField = ({
  hint,
  passwordMode,
  startIcon,
  onChange,
  dateMode,
  onlyNumbers,
  maxLength,
  onFocus,
  expiryMode,
  disabled,
  error,
  textValue = "",
  titleCase,
  inputRef,
  onKeyDown,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState("");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toTitleCase = (text) => {
    if (!text) return "";
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleChange = (event) => {
    const val = event.target.value;
    if (dateMode) {
      const onlyNumbers = val.replace(/\D/g, "");
      if (onlyNumbers.length > 8) return;
      let formattedValue = onlyNumbers;
      if (onlyNumbers.length == 2 && val.slice(2, 3) == "/") {
        formattedValue = `${onlyNumbers.slice(0, 2)}/`;
      }
      if (onlyNumbers.length == 2 && val.length >= value.length) {
        formattedValue = `${onlyNumbers.slice(0, 2)}/`;
      }
      if (onlyNumbers.length >= 3) {
        formattedValue = `${onlyNumbers.slice(0, 2)}/${onlyNumbers.slice(2)}`;
      }
      if (onlyNumbers.length == 4 && val.slice(5, 6) == "/") {
        formattedValue = `${onlyNumbers.slice(0, 2)}/${onlyNumbers.slice(2, 4)}/`;
      }
      if (onlyNumbers.length == 4 && val.length >= value.length) {
        formattedValue = `${onlyNumbers.slice(0, 2)}/${onlyNumbers.slice(2, 4)}/`;
      }
      if (onlyNumbers.length >= 5) {
        formattedValue = `${onlyNumbers.slice(0, 2)}/${onlyNumbers.slice(2, 4)}/${onlyNumbers.slice(4)}`;
      }
      if (onlyNumbers.length == 1 && onlyNumbers > 3) formattedValue = `0${onlyNumbers}/`;
      if (onlyNumbers.length == 2 && onlyNumbers > 31) return;
      if (onlyNumbers.length == 3 && onlyNumbers.slice(2, 3) >= 2)
        formattedValue = `${onlyNumbers.slice(0, 2)}/0${onlyNumbers.slice(2)}`;
      if (onlyNumbers.length == 4 && onlyNumbers.slice(2, 4) > 12) return;
      if (onlyNumbers.length == 5 && onlyNumbers.slice(4, 5) >= 3)
        formattedValue = `${onlyNumbers.slice(0, 2)}/${onlyNumbers.slice(2, 4)}/19${onlyNumbers.slice(4)}`;
      if (onlyNumbers.length == 6 && (onlyNumbers.slice(4, 6) < 19 || onlyNumbers.slice(4, 6) > 20)) return;
      if (onlyNumbers.length == 8 && onlyNumbers.slice(4, 8) > 2024) return;
      setValue(formattedValue);
      if (onChange) onChange(formattedValue);
      return;
    } else if (onlyNumbers) {
      const onlyNumbers = val.replace(/\D/g, "");
      if (maxLength && onlyNumbers.length > maxLength) return;
      setValue(onlyNumbers);
      if (onChange) onChange(onlyNumbers);
      return;
    } else if (expiryMode) {
      const onlyNumbers = val.replace(/\D/g, "");
      if (onlyNumbers.length > 4) return;
      let formattedValue = onlyNumbers;
      if (onlyNumbers.length == 2 && val.slice(2, 3) == "/") {
        formattedValue = `${onlyNumbers.slice(0, 2)}/`;
      }
      if (onlyNumbers.length >= 3) {
        formattedValue = `${onlyNumbers.slice(0, 2)}/${onlyNumbers.slice(2)}`;
      }
      if (onlyNumbers.length == 1 && onlyNumbers >= 2) formattedValue = `0${onlyNumbers}/`;
      if (onlyNumbers.length == 2 && (onlyNumbers.slice(0, 2) > 12 || onlyNumbers.slice(0, 2) < 1)) return;
      setValue(formattedValue);
      if (onChange) onChange(formattedValue);
      return;
    }
    if (maxLength && val.length > maxLength) return;

    setValue(val);
    if (onChange) {
      onChange(val);
    }
  };

  return (
    <TextField
      variant="standard"
      placeholder={hint}
      inputRef={inputRef}
      onKeyDown={onKeyDown}
      onFocus={() => {
        setFocused(true);
        if (onFocus) onFocus(true);
      }}
      onBlur={() => {
        setFocused(false);
        if (onFocus) onFocus(false);
      }}
      style={{ width: "100%", maxWidth: "400px" }}
      onChange={handleChange} // Usa la función handleChange
      value={titleCase ? toTitleCase(value || textValue) : value || textValue}
      disabled={disabled}
      pattern={dateMode || expiryMode ? "[0-9]*" : onlyNumbers ? "[0-9]*" : undefined}
      inputProps={{
        maxLength: dateMode || expiryMode ? 10 : maxLength ? maxLength : undefined,
        pattern: dateMode || expiryMode ? "[0-9]*" : onlyNumbers ? "[0-9]*" : undefined,
        type: "text",
      }}
      InputProps={{
        disableUnderline: true,
        style: {
          backgroundColor: "#1F222A",
          borderRadius: "10px",
          fontSize: "18px",
          fontFamily: "Urbanist",
          fontWeight: "regular",
          color: "rgba(255, 255, 255, 1)",
          border: "none",
          outline: "none",
          padding: "10px 20px",
          minHeight: "45px",
        },
        inputProps: {
          type: "text",
          pattern: dateMode || expiryMode ? "[0-9]*" : onlyNumbers ? "[0-9]*" : undefined,
          style: {
            WebkitTextSecurity: !passwordMode || showPassword ? "none" : "disc",
            paddingLeft: startIcon ? "10px" : "0",
          },
        },
        type: "text",
        pattern: dateMode || expiryMode ? "[0-9]*" : onlyNumbers ? "[0-9]*" : undefined,
        startAdornment: startIcon
          ? React.cloneElement(startIcon, {
              style: {
                color: focused ? "#FF6401" : error ? "#FF1F01" : "rgba(255, 255, 255, 0.5)",
              },
            })
          : null,
        endAdornment: passwordMode ? (
          <InputAdornment position="end">
            <IconButton
              onClick={togglePasswordVisibility}
              style={{
                color: !focused ? "rgba(255, 255, 255, 0.5)" : "#FF6401",
              }}
            >
              {!showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        "& .MuiInputBase-root": {
          backgroundColor: "#1F222A",
          borderRadius: "10px",
          transition: "border 0.3s, box-shadow 0.3s",
        },
        "& .MuiInputBase-root:focus-within": {
          boxShadow: "0 0 0 2px #FF6401", // Sombra cuando el input está en foco
          border: "2px solid #FF6401", // Borde cuando el input está en foco
        },
        "& input:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0 30px #1F222A inset", // Elimina el fondo del autocompletado
          WebkitTextFillColor: "rgba(255, 255, 255, 1)", // Cambia el color del texto
        },
        "& .Mui-disabled": {
          WebkitTextFillColor: "rgba(255, 255, 255, 1)", // Mantiene el color del texto cuando está deshabilitado
          color: "rgba(255, 255, 255, 1)", // Color del texto en otros navegadores
        },
      }}
    />
  );
};

export default CustomTextField;
